import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import "./SavedBlogs.css";
import BannerAds from "./BannerAds";

function SavedBlogs() {
  const [savedBlogs, setSavedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [followStatus, setFollowStatus] = useState({});
  const [followLoading, setFollowLoading] = useState({});

  // PROFILE PICS
  const [authorProfiles, setAuthorProfiles] = useState({});

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const BLOGS_PER_PAGE = 12;

  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);

  const videoMap = useRef({});
  const playingId = useRef(null);

  const navigate = useNavigate();

  const BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://success-blog-app-backend.onrender.com";

  const COLORS = [
    "#6332ff",
    "#00ffc8",
    "#f472b6",
    "#60a5fa",
    "#a78bfa",
  ];

  const makeParticle = useCallback(
    (W, H) => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.8 + 0.4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: Math.random() * 0.6 + 0.2,
      pulse: Math.random() * Math.PI * 2,
    }),
    []
  );

  // =========================
  // PARTICLE BACKGROUND
  // =========================
  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;

    if (!canvas || !root) return;

    const ctx = canvas.getContext("2d");

    let W = root.offsetWidth;
    let H = root.offsetHeight;

    const resize = () => {
      W = root.offsetWidth;
      H = root.offsetHeight;

      canvas.width = W;
      canvas.height = H;
    };

    resize();

    particlesRef.current = Array.from(
      { length: 90 },
      () => makeParticle(W, H)
    );

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      const particles = particlesRef.current;

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.018;

        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;

        const pa =
          p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pa;
        ctx.fill();
      });

      ctx.globalAlpha = 1;

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;

          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = particles[i].color;
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = (1 - dist / 100) * 0.12;
            ctx.stroke();
          }
        }
      }

      ctx.globalAlpha = 1;

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    const ro = new ResizeObserver(resize);

    ro.observe(root);

    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  // =========================
  // SINGLE VIDEO PLAY
  // =========================
  const onVideoPlay = useCallback((id) => {
    Object.keys(videoMap.current).forEach((vid) => {
      if (vid !== id) {
        const el = videoMap.current[vid];

        if (el && !el.paused) {
          el.pause();
        }
      }
    });

    playingId.current = id;
  }, []);

  const registerVideo = useCallback((id, el) => {
    if (el) {
      videoMap.current[id] = el;
    } else {
      delete videoMap.current[id];
    }
  }, []);

  // =========================
  // FETCH AUTHOR PROFILE PIC
  // =========================
  const fetchAuthorProfile = async (authorName) => {
    try {
      if (authorProfiles[authorName]) return;

      const res = await axios.get(
        `${BASE_URL}/api/profile/username/${authorName}`
      );

      let pic = res.data.profilePic || "";

      // FIX PROFILE PIC URL
      if (pic && !pic.startsWith("http")) {
        pic = `${BASE_URL}/${pic}`;
      }

      setAuthorProfiles((prev) => ({
        ...prev,
        [authorName]: pic,
      }));
    } catch (err) {
      console.log("No profile for:", authorName);
    }
  };

  // =========================
  // FETCH BLOGS
  // =========================
  const fetchSavedBlogs = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${BASE_URL}/api/blogs/saved`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const blogs = (
        res.data.savedBlogs || []
      ).reverse();

      setSavedBlogs(blogs);

      const uniqueAuthors = [
        ...new Set(
          blogs.map((b) => b.author).filter(Boolean)
        ),
      ];

      // FETCH PROFILE PICS
      uniqueAuthors.forEach(fetchAuthorProfile);

      checkFollowStatuses(uniqueAuthors, token);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const checkFollowStatuses = async (
    authors,
    token
  ) => {
    const statuses = {};

    await Promise.all(
      authors.map(async (author) => {
        try {
          const res = await axios.get(
            `${BASE_URL}/api/users/${author}/follow-status`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          statuses[author] = res.data.isFollowing;
        } catch {
          statuses[author] = false;
        }
      })
    );

    setFollowStatus(statuses);
  };

  useEffect(() => {
    fetchSavedBlogs();
  }, []);

  // =========================
  // PAGINATION LOGIC
  // =========================
  const totalPages = Math.ceil(
    savedBlogs.length / BLOGS_PER_PAGE
  );

  const currentBlogs = useMemo(() => {
    const start =
      (currentPage - 1) * BLOGS_PER_PAGE;

    const end = start + BLOGS_PER_PAGE;

    return savedBlogs.slice(start, end);
  }, [savedBlogs, currentPage]);

  const changePage = (page) => {
    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================
  // ACTIONS
  // =========================
  const toggleReadMore = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const removeBlog = async (id) => {
    try {
      const token = localStorage.getItem("token");

      await axios.post(
        `${BASE_URL}/api/blogs/${id}/save`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      delete videoMap.current[id];

      if (playingId.current === id) {
        playingId.current = null;
      }

      const updated = savedBlogs.filter(
        (b) => b._id !== id
      );

      setSavedBlogs(updated);

      const newTotalPages = Math.ceil(
        updated.length / BLOGS_PER_PAGE
      );

      if (
        currentPage > newTotalPages &&
        currentPage > 1
      ) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleFollow = async (authorName) => {
    if (
      !authorName ||
      followLoading[authorName]
    )
      return;

    const token = localStorage.getItem("token");

    const current = followStatus[authorName];

    setFollowStatus((prev) => ({
      ...prev,
      [authorName]: !current,
    }));

    setFollowLoading((prev) => ({
      ...prev,
      [authorName]: true,
    }));

    try {
      await axios.post(
        `${BASE_URL}/api/users/${authorName}/follow`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch {
      setFollowStatus((prev) => ({
        ...prev,
        [authorName]: current,
      }));
    } finally {
      setFollowLoading((prev) => ({
        ...prev,
        [authorName]: false,
      }));
    }
  };

  const goToProfile = (authorName) => {
    if (authorName) {
      navigate(`/authorprofile/${authorName}`);
    }
  };

  const totalLikes = savedBlogs.reduce(
    (sum, b) =>
      sum +
      (Array.isArray(b.likes)
        ? b.likes.length
        : b.likes || 0),
    0
  );

  const uniqueAuthorsCount = new Set(
    savedBlogs
      .map((b) => b.author)
      .filter(Boolean)
  ).size;

  const GLOW_COLORS = [
    "#6332ff",
    "#00ffc8",
    "#f472b6",
    "#60a5fa",
    "#a78bfa",
  ];

  return (
    <>
      <Navbar />

      <div className="sb-root" ref={rootRef}>
        <canvas
          className="sb-canvas"
          ref={canvasRef}
        />

        <div className="sb-aurora" />

        {/* HEADER */}
        <div className="sb-header">
          <div className="sb-header-label">
            ✦ neural library
          </div>

          <h2>Saved Blogs</h2>

          <p>Your curated intelligence feed</p>
        </div>

        {/* STATS */}
        <div className="sb-stat-row">
          <div className="sb-stat">
            <span className="sb-stat-num">
              {savedBlogs.length}
            </span>

            <span className="sb-stat-label">
              Saved
            </span>
          </div>

          <div className="sb-stat">
            <span className="sb-stat-num">
              {uniqueAuthorsCount}
            </span>

            <span className="sb-stat-label">
              Authors
            </span>
          </div>

          <div className="sb-stat">
            <span className="sb-stat-num">
              {totalLikes}
            </span>

            <span className="sb-stat-label">
              Total Likes
            </span>
          </div>
        </div>

        {/* LOADING */}
        {loading ? (
          <div className="sb-loading">
            <span className="sb-loading-dot" />
            <span className="sb-loading-dot" />
            <span className="sb-loading-dot" />
          </div>
        ) : savedBlogs.length === 0 ? (
          <div className="sb-empty">
            [ NO SAVED ENTRIES ]
          </div>
        ) : (
          <>
            {/* BLOG GRID */}
            <div className="sb-grid">
              {currentBlogs.map((blog, idx) => {
                const isExpanded =
                  expanded[blog._id];

                const isFollowing =
                  followStatus[blog.author];

                const isFollowLoading =
                  followLoading[blog.author];

                const likesCount = Array.isArray(
                  blog.likes
                )
                  ? blog.likes.length
                  : blog.likes || 0;

                const glowColor =
                  GLOW_COLORS[
                    idx % GLOW_COLORS.length
                  ];

                const rawContent = (
                  blog.content || ""
                ).trim();

                const hasContent =
                  rawContent.length > 0;

                const needsReadMore =
                  hasContent &&
                  rawContent.length > 200;

                // FIX VIDEO URL
                const videoSrc = blog.video
                  ? blog.video.startsWith("http")
                    ? blog.video
                    : `${BASE_URL}/${blog.video}`
                  : null;

                // FIX IMAGE URL
                const imageSrc = blog.image
                  ? blog.image.startsWith("http")
                    ? blog.image
                    : `${BASE_URL}/${blog.image}`
                  : null;

                // PROFILE PIC
                const profilePic =
                  authorProfiles[blog.author] ||
                  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

                return (
                  <div
                    className="sb-card"
                    key={blog._id}
                    style={{
                      animationDelay: `${
                        idx * 0.07
                      }s`,
                    }}
                  >
                    <div className="sb-scanline" />

                    <div
                      className="sb-card-glow"
                      style={{
                        background: glowColor,
                      }}
                    />

                    {/* <span className="sb-badge">
                      SAVED
                    </span> */}

                    {/* AUTHOR */}
                    <div className="sb-author-row">
                      <div
                        className="sb-author-left"
                        onClick={() =>
                          goToProfile(
                            blog.author
                          )
                        }
                      >
                        {/* PROFILE PIC */}
                        <img
                          src={profilePic}
                          alt="profile"
                          className="sb-profile-pic"
                        />

                        <div>
                          <h4 className="sb-author-name">
                            {blog.author}
                          </h4>

                          <span className="sb-date">
                            {new Date(
                              blog.createdAt
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <button
                        className={`sb-follow-btn ${
                          isFollowing
                            ? "following"
                            : ""
                        }`}
                        onClick={() =>
                          toggleFollow(
                            blog.author
                          )
                        }
                        disabled={
                          isFollowLoading
                        }
                      >
                        {isFollowing
                          ? "Following"
                          : "Follow"}
                      </button>
                    </div>

                    {/* IMAGE */}
                    {imageSrc && !videoSrc && (
                      <img
                        src={imageSrc}
                        alt="blog cover"
                        className="sb-media"
                        loading="lazy"
                      />
                    )}

                    {/* VIDEO */}
                    {videoSrc && (
                      <div className="sb-video-wrap">
                        <video
                          ref={(el) =>
                            registerVideo(
                              blog._id,
                              el
                            )
                          }
                          src={videoSrc}
                          controls
                          controlsList="nodownload"
                          onContextMenu={(e) =>
                            e.preventDefault()
                          }
                          onPlay={() =>
                            onVideoPlay(
                              blog._id
                            )
                          }
                          className="sb-video"
                          playsInline
                          preload="metadata"
                        />
                      </div>
                    )}

                    {/* TITLE */}
                    <h3 className="sb-title">
                      {blog.title}
                    </h3>

                    {/* CONTENT */}
                    {hasContent && (
                      <p className="sb-content">
                        {isExpanded
                          ? rawContent
                          : rawContent.slice(
                              0,
                              200
                            )}

                        {needsReadMore && (
                          <button
                            className="sb-readmore"
                            onClick={() =>
                              toggleReadMore(
                                blog._id
                              )
                            }
                          >
                            {isExpanded
                              ? " Read Less"
                              : " ...Read More"}
                          </button>
                        )}
                      </p>
                    )}

                    {/* FOOTER */}
                    <div className="sb-footer">
                      <div className="sb-likes">
                        ❤️ {likesCount}
                      </div>

                      <button
                        className="sb-remove-btn"
                        onClick={() =>
                          removeBlog(blog._id)
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="sb-pagination">
                <button
                  className="sb-page-btn"
                  disabled={currentPage === 1}
                  onClick={() =>
                    changePage(
                      currentPage - 1
                    )
                  }
                >
                  Prev
                </button>

                {[...Array(totalPages)].map(
                  (_, index) => (
                    <button
                      key={index}
                      className={`sb-page-btn ${
                        currentPage ===
                        index + 1
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        changePage(index + 1)
                      }
                    >
                      {index + 1}
                    </button>
                  )
                )}

                <button
                  className="sb-page-btn"
                  disabled={
                    currentPage === totalPages
                  }
                  onClick={() =>
                    changePage(
                      currentPage + 1
                    )
                  }
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SavedBlogs;